import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, Plus, Play, Trash2, TrendingUp, Eye, Edit, PlayCircle, Loader2 } from 'lucide-react';
import api from '../services/api';
import QuestionSuggestions from '../components/QuestionSuggestions';
import PromptLibrary from '../components/PromptLibrary';
import EnginePill from '../components/EnginePill';
import BrandIcon from '../components/BrandIcon';

const Keywords = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [website, setWebsite] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [scheduleFrequency, setScheduleFrequency] = useState('weekly');
  const [country, setCountry] = useState('US');
  const [editingKeyword, setEditingKeyword] = useState(null);
  const [editScheduleFrequency, setEditScheduleFrequency] = useState('weekly');
  const [editCountry, setEditCountry] = useState('US');
  const [submitting, setSubmitting] = useState(false);
  const [runningQueries, setRunningQueries] = useState(new Set());
  const [runAllStatus, setRunAllStatus] = useState('idle'); // idle | running | done
  const [runAllResult, setRunAllResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, [websiteId]);

  const fetchData = async () => {
    try {
      const [websiteResponse, keywordsResponse] = await Promise.all([
        api.get(`/websites/${websiteId}`),
        api.get(`/keywords/website/${websiteId}`)
      ]);

      setWebsite(websiteResponse.data.website);
      setKeywords(keywordsResponse.data.keywords || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddKeyword = async () => {
    if (!newKeyword.trim()) return;

    setSubmitting(true);
    try {
      await api.post('/keywords', {
        keyword: newKeyword,
        websiteId: websiteId,
        scheduleFrequency: scheduleFrequency,
        country: country
      });
      setNewKeyword('');
      setScheduleFrequency('weekly');
      setCountry('US');
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add keyword');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRunAll = async () => {
    setRunAllStatus('running');
    setError('');
    try {
      const response = await api.post(`/admin/batch-process/${websiteId}`);
      setRunAllResult(response.data);
      setRunAllStatus('done');
      // Auto-refresh after 5 seconds so the user sees runs starting to land
      setTimeout(() => fetchData(), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start batch run');
      setRunAllStatus('idle');
    }
  };

  const handleRunQuery = async (keywordId) => {
    setRunningQueries(prev => new Set(prev.add(keywordId)));

    try {
      await api.post(`/queries/keyword/${keywordId}`);
      // Wait a bit then refresh the data
      setTimeout(() => {
        fetchData();
        setRunningQueries(prev => {
          const newSet = new Set(prev);
          newSet.delete(keywordId);
          return newSet;
        });
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to run query');
      setRunningQueries(prev => {
        const newSet = new Set(prev);
        newSet.delete(keywordId);
        return newSet;
      });
    }
  };

  const handleEditKeyword = (keyword) => {
    setEditingKeyword(keyword);
    setEditScheduleFrequency(keyword.scheduleFrequency || 'weekly');
    setEditCountry(keyword.country || 'US');
    setEditDialogOpen(true);
  };

  const handleUpdateKeyword = async () => {
    if (!editingKeyword) return;

    setSubmitting(true);
    try {
      await api.put(`/keywords/${editingKeyword.id}`, {
        scheduleFrequency: editScheduleFrequency,
        country: editCountry
      });
      setEditDialogOpen(false);
      setEditingKeyword(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update keyword');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteKeyword = async (keywordId) => {
    if (!window.confirm('Are you sure you want to delete this keyword?')) return;

    try {
      await api.delete(`/keywords/${keywordId}`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete keyword');
    }
  };

  const handleViewDetails = (keywordId) => {
    navigate(`/keyword/${keywordId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!website) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Website not found</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/websites')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex flex-1 items-center gap-3 flex-wrap">
          <BrandIcon website={website}/><h1 className="text-3xl font-bold tracking-tight">
            Keywords for {website.brandName}
          </h1>
          <EnginePill />
        </div>
        <div className="flex gap-2 flex-wrap">
          <PromptLibrary key={`library-${websiteId}`} websiteId={websiteId} website={website} keywords={keywords} onAdded={fetchData} researchRequestId={location.state?.promptSetup?.requestId} />
          <QuestionSuggestions key={websiteId} websiteId={websiteId} keywords={keywords} onAdded={fetchData} />
          <Button
            variant="outline"
            onClick={handleRunAll}
            disabled={runAllStatus === 'running' || keywords.length === 0}
          >
            {runAllStatus === 'running' ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Starting…
              </>
            ) : (
              <>
                <PlayCircle className="h-4 w-4 mr-2" />
                Run all
              </>
            )}
          </Button>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Keyword
          </Button>
        </div>
      </div>

      {runAllStatus === 'done' && runAllResult && (
        <Alert className="border-emerald-500 bg-emerald-50 text-emerald-900">
          <AlertDescription>
            <strong>Batch run started.</strong> Processing {runAllResult.total} keyword{runAllResult.total === 1 ? '' : 's'} in the background. Refresh in a few minutes — runs will land as they complete.
            <Button
              size="sm"
              variant="ghost"
              className="ml-3"
              onClick={() => { setRunAllStatus('idle'); setRunAllResult(null); }}
            >
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Alert>
        <AlertTitle>⚠️ API Cost Notice</AlertTitle>
        <AlertDescription>
          Each keyword query uses OpenAI API credits. Running queries frequently will incur additional costs. Monitor your usage carefully.
        </AlertDescription>
      </Alert>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {keywords.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <h2 className="text-xl font-semibold text-muted-foreground mb-2">
              No keywords added yet
            </h2>
            <p className="text-muted-foreground mb-4">
              Add your first keyword to start tracking citations
            </p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Keyword
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Keyword</TableHead>
                <TableHead>
                  <span title="Monthly Google search volume (Ahrefs). NOT the same as how often this is asked of ChatGPT or other AI engines — this is traditional organic search demand.">
                    Google volume <span className="text-[10px] text-muted-foreground">(organic, US)</span>
                  </span>
                </TableHead>
                <TableHead>Schedule</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Total Runs</TableHead>
                <TableHead>Last Run</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {keywords.map((keyword) => (
                <TableRow key={keyword.id}>
                  <TableCell className="font-medium">{keyword.keyword}</TableCell>
                  <TableCell>
                    {keyword.organicVolume === null || keyword.organicVolume === undefined ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      <span className={`text-sm font-medium ${keyword.organicVolume === 0 ? 'text-muted-foreground' : ''}`}>
                        {keyword.organicVolume.toLocaleString()}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {keyword.scheduleFrequency === 'daily' && 'Daily'}
                      {keyword.scheduleFrequency === 'weekly' && 'Weekly'}
                      {keyword.scheduleFrequency === 'monthly' && 'Monthly'}
                    </Badge>
                  </TableCell>
                  <TableCell>{keyword.country || 'US'}</TableCell>
                  <TableCell>
                    {keyword.isActive ? (
                      <Badge variant="success">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>{keyword.totalRuns || 0}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(keyword.lastRunAt)}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleViewDetails(keyword.id)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEditKeyword(keyword)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRunQuery(keyword.id)}
                        disabled={runningQueries.has(keyword.id)}
                      >
                        {runningQueries.has(keyword.id) ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteKeyword(keyword.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add Keyword Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Keyword</DialogTitle>
            <DialogDescription>
              Enter a keyword to track citations for {website.brandName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Keyword</label>
              <Input
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                placeholder="Enter keyword..."
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleAddKeyword()}
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium">Schedule Frequency</label>
              <Select value={scheduleFrequency} onValueChange={setScheduleFrequency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                How often to run queries for this keyword
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Country</label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="US">United States</SelectItem>
                  <SelectItem value="GB">United Kingdom</SelectItem>
                  <SelectItem value="CA">Canada</SelectItem>
                  <SelectItem value="AU">Australia</SelectItem>
                  <SelectItem value="DE">Germany</SelectItem>
                  <SelectItem value="FR">France</SelectItem>
                  <SelectItem value="ES">Spain</SelectItem>
                  <SelectItem value="IT">Italy</SelectItem>
                  <SelectItem value="NL">Netherlands</SelectItem>
                  <SelectItem value="SE">Sweden</SelectItem>
                  <SelectItem value="NO">Norway</SelectItem>
                  <SelectItem value="DK">Denmark</SelectItem>
                  <SelectItem value="FI">Finland</SelectItem>
                  <SelectItem value="IE">Ireland</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Target country for AI searches
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddKeyword} disabled={submitting || !newKeyword.trim()}>
              {submitting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : null}
              Add Keyword
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Keyword Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Keyword</DialogTitle>
            <DialogDescription>
              Update schedule and country for "{editingKeyword?.keyword}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Schedule Frequency</label>
              <Select value={editScheduleFrequency} onValueChange={setEditScheduleFrequency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                How often to run queries for this keyword
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Country</label>
              <Select value={editCountry} onValueChange={setEditCountry}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="US">United States</SelectItem>
                  <SelectItem value="GB">United Kingdom</SelectItem>
                  <SelectItem value="CA">Canada</SelectItem>
                  <SelectItem value="AU">Australia</SelectItem>
                  <SelectItem value="DE">Germany</SelectItem>
                  <SelectItem value="FR">France</SelectItem>
                  <SelectItem value="ES">Spain</SelectItem>
                  <SelectItem value="IT">Italy</SelectItem>
                  <SelectItem value="NL">Netherlands</SelectItem>
                  <SelectItem value="SE">Sweden</SelectItem>
                  <SelectItem value="NO">Norway</SelectItem>
                  <SelectItem value="DK">Denmark</SelectItem>
                  <SelectItem value="FI">Finland</SelectItem>
                  <SelectItem value="IE">Ireland</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Target country for AI searches
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateKeyword} disabled={submitting}>
              {submitting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : null}
              Update Keyword
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Keywords;
